import type { Market } from '@/lib/market/types';

/**
 * =============================================================================
 *  `GET /meta/metrics` 的 `nav` → 側邊欄的連結。
 *
 *  ROADMAP §6 交付項逐字：「Sidebar **完全**由 `GET /meta/metrics` 的 `nav` 產生，
 *  **前端不得有任何硬寫的 metric 清單**（以 grep 驗證）」，驗收 #2：
 *
 *      grep -rn "<驗收 #2 的那兩個 metric key>" frontend/src --include=*.tsx
 *
 *  （那兩個 key 在這裡刻意寫成佔位符：**grep 不區分程式碼與註解**，
 *   把它們寫進註解等於讓驗收自己失敗。完整指令在 ROADMAP §6 驗收 #2。）
 *
 *  在 route 與元件檔中必須抓不到。所以這個模組把 registry 的 `href_template`
 *  翻成 TanStack Router 的 `{ to, params }`，而**它只認路徑的形狀，不認 metric_key**。
 *
 *  為什麼需要翻譯而不是直接 `<a href>`
 *  ----------------------------------
 *  `href_template` 是一個**已經代入市場**的絕對路徑（`/tw/securities/{symbol}/...`）。
 *  用 `<a href>` 直接送出去會變成整頁重新載入：query 快取、Zustand、圖表全部重來，
 *  而且 `?as_of=` 不會被 `retainSearchParams` 帶著走——時光機會在點側邊欄的瞬間悄悄歸零。
 *  翻成 `<Link to params>` 之後它是一次 client-side 導覽，`as_of` 跟著走。
 *
 *  ⚠️ 這裡有一張表，但它是 **URL 區段 → 路由**，不是 metric → 頁面。
 *  它是後端 `metric_resolver._STUDIO_SEGMENT`（category → 區段）的鏡像，
 *  兩邊都只有五個字串，而且都不含任何 metric_key。新增一個 metric 不需要碰它；
 *  新增一個**類別**才需要，而那本來就要在兩端各加一列。
 * ========================================================================== */

/** 一個側邊欄項目要嘛導覽得動，要嘛明說它還沒上線——不會有第三種狀態。 */
export type NavDestination =
  | {
      readonly kind: 'route';
      /** TanStack Router 的 `to`（含 `$` 參數）。 */
      readonly to: string;
      readonly params: Record<string, string>;
      readonly search?: Record<string, string> | undefined;
    }
  | {
      readonly kind: 'unavailable';
      /** 給使用者看的一句話。空連結比灰掉的連結糟：它看起來像壞掉。 */
      readonly reason: string;
    };

/**
 * URL 區段 → 路由樣板。**四條路由共用同一個 `MetricStudioPage`**（§6 交付項）。
 *
 * `chart` 是 `category='technical'/'macro'` 與價格本身的落點；它在 P3 還沒有
 * 全螢幕圖表頁，最接近的既有目的地是儀表板，所以指過去而不是留一個 404。
 */
const STUDIO_SECTIONS: Record<string, string> = {
  chips: '/$market/securities/$symbol/chips/$metricKey',
  fundamental: '/$market/securities/$symbol/fundamental/$metricKey',
  valuation: '/$market/securities/$symbol/valuation/$metricKey',
};

const MARKET_STUDIO_ROUTE = '/$market/market/$metricKey';
const DASHBOARD_ROUTE = '/$market/dashboard';

/**
 * `href_template` → 目的地。
 *
 * @param hrefTemplate registry 的 `href_template`（`{symbol}` 是唯一佔位符）
 * @param market       目前市場（路徑第一段其實也是它，但以呼叫端為準）
 * @param symbol       目前個股；`grain='market'` 的項目用不到
 */
export function navDestination(
  hrefTemplate: string,
  market: Market,
  symbol: string | undefined,
): NavDestination {
  const segments = hrefTemplate.split('/').filter((s) => s !== '');
  // 第一段是市場（`/tw/...`）。以呼叫端的 market 為準——側邊欄在美股頁面上
  // 顯示的是美股的 registry，兩者必然一致；不一致時跟著 URL 走才是對的。
  const rest = segments.slice(1);

  if (rest[0] === 'market' && rest.length === 2) {
    if (rest[1] === 'sector-rotation') {
      return { kind: 'route', to: '/$market/market/sector-rotation', params: { market } };
    }
    return { kind: 'route', to: MARKET_STUDIO_ROUTE, params: { market, metricKey: rest[1] } };
  }

  if (rest[0] === 'sector' && rest.length === 2) {
    return { kind: 'route', to: '/$market/market/sector-rotation', params: { market } };
  }

  if (rest[0] === 'securities' && rest[1] === '{symbol}') {
    if (symbol === undefined || symbol === '') {
      return { kind: 'unavailable', reason: '先選一檔股票' };
    }
    const section = rest[2];
    if (section === undefined) {
      return { kind: 'unavailable', reason: '這個登錄列沒有指定頁面區段' };
    }
    if (rest[2] === 'broker-branches') {
      return {
        kind: 'route',
        to: '/$market/securities/$symbol/broker-branches',
        params: { market, symbol },
      };
    }
    // `/tw/securities/{symbol}/chart` —— 沒有 metric 區段，落在儀表板。
    if (rest.length === 3) {
      return {
        kind: 'route',
        to: DASHBOARD_ROUTE,
        params: { market },
        search: { symbol },
      };
    }
    // category='model' 的目的地（metric_resolver.href_template）。它不是 Metric Studio：
    // 模型數字只能在 <ModelOutput> 裡渲染，所以它有自己的頁面，也沒有 $metricKey。
    if (rest[2] === 'ai' && rest[3] === 'prediction' && rest.length === 4) {
      return {
        kind: 'route',
        to: '/$market/securities/$symbol/ai/prediction',
        params: { market, symbol },
      };
    }
    const to = STUDIO_SECTIONS[section];
    if (to === undefined) {
      return { kind: 'unavailable', reason: '這一頁在後續階段上線' };
    }
    return { kind: 'route', to, params: { market, symbol, metricKey: rest[3] } };
  }

  return { kind: 'unavailable', reason: '這一頁在後續階段上線' };
}

/**
 * 目前這一條 URL 對應到哪一個 metric_key？側邊欄用它標示「你在這裡」。
 *
 * 比對的是**參數**而不是字串前綴：任何一個 key 與「那個 key 再接一段後綴」
 * 的前綴會互相誤判（側邊欄同時亮兩列），而參數比對不會。
 */
export function isActiveDestination(
  destination: NavDestination,
  current: { readonly metricKey: string | undefined },
): boolean {
  if (destination.kind !== 'route') return false;
  const target = destination.params['metricKey'];
  return target !== undefined && target === current.metricKey;
}
